import { useEffect, useRef, useState } from 'react';
import type { Language } from '@accounts/locale';
import { languages } from '@accounts/locale';
import { usePresence } from '@accounts/session';
import type { Choice } from '@accounts/theme';
import { Refusal } from '@accounts/transport';
import { Button, Callout, Field, Form, Select } from '@beyond-js/ui/react';
import { Screen } from '@accounts/navigation';
import { Outage, Panel, Problem } from '@accounts/ui';
import { Mail } from './account/mail';

/**
 * A person's own account: their name, and the language and appearance every Beyond product uses.
 *
 * This is the one place a person's language and appearance are changed for all of Beyond (D07): every
 * product applies them when it learns who the person is, and links here as "Change for all of Beyond"
 * from its own "Language and appearance" dialog. They stay on this page, as fields of the form width
 * (D54); this interface's own bar offers no such dialog.
 *
 * None of it affects any authorization decision, which is why it is the one place a person changes
 * something about themselves without proving their identity again. The screen says what is saved and
 * when, and never says "saved" before the service has said so. Its one read, the email choice, joins the
 * page's notice when Accounts does not answer it (`Outage`, D163). A name cleared is a step not yet taken (D183):
 * Save waits quietly and a press puts the caret in it, sending nothing.
 */
export function Account() {
	const { text, identity, accounts, refresh, language, choose, appearance, look, unsaved } = usePresence();

	const [name, setName] = useState(identity?.account.name ?? '');
	const named = useRef<HTMLInputElement>(null);
	const [busy, setBusy] = useState(false);
	const [notice, setNotice] = useState<string>();
	const [failure, setFailure] = useState<unknown>();

	useEffect(() => {
		setName(identity?.account.name ?? '');
	}, [identity?.account.name]);

	const changed = name.trim() !== (identity?.account.name ?? '');

	async function save() {
		setBusy(true);
		setNotice(undefined);
		setFailure(undefined);
		try {
			await accounts.prefer({ name: name.trim() });
			// The change was accepted; when only the re-read failed, saying "saved" alone would let
			// someone believe the stale values on screen are current
			setNotice(text((await refresh()) ? 'state.saved' : 'state.savedStale'));
		} catch (error) {
			// Withheld by the page's session, which answers the person: the form stays as it was
			if (Refusal.is(error) && error.quiet) return;
			setFailure(error);
			// Nothing came back, so the name may have changed: it is read again
			if (Refusal.is(error) && error.unknown) await refresh();
		} finally {
			setBusy(false);
		}
	}

	return (
		<Screen template="settings" width="form" title={text('account.title')} lead={text('account.lead')}>
			<Outage>
				{failure ? <Problem error={failure} /> : null}
				{notice ? <Callout tone="success" live title={notice} /> : null}

				<Panel title={text('account.details')}>
					<Form
						className="flex flex-col gap-4"
						onSubmit={event => {
							event.preventDefault();
							void save();
						}}
					>
						<Field label={text('account.name')} hint={text('account.name.help')}>
							<input ref={named} value={name} maxLength={100} required onChange={event => setName(event.target.value)} />
						</Field>
						<div>
							{/* Nothing changed yet: Save waits quietly, and a press puts the caret in the name (D183) */}
							<Button type="submit" variant="primary" busy={busy} labels={{ busy: text('action.saving') }} waiting={!changed} place={() => named.current}>
								{text('action.save')}
							</Button>
						</div>
					</Form>
				</Panel>

				<Panel title={text('account.preferences')} description={text('account.preferences.lead')}>
					<div className="flex flex-col gap-4">
						{/* A change this device applied that did not reach the account is said, not swallowed (ACC-13) */}
						{unsaved ? <Problem error={unsaved} tone="warning" title={text('account.preferences.unsaved')} /> : null}
						<Field label={text('account.language')}>
							<Select
								value={language}
								onChange={event => void choose(event.target.value as Language)}
								options={languages.map(code => ({ value: code, label: text(`language.${code}`) }))}
							/>
						</Field>
						<Field label={text('account.appearance')}>
							<Select
								value={appearance}
								onChange={event => void look(event.target.value as Choice)}
								options={(['system', 'light', 'dark'] as const).map(value => ({ value, label: text(`appearance.${value}`) }))}
							/>
						</Field>
					</div>
				</Panel>
				<Mail />
			</Outage>
		</Screen>
	);
}
